/**
 * ==== STANDALONE:FARMHAND_PRO:START ====
 * Feature: AI chat interface with Hank & Laura
 * Tier: Pro ($7/mo)
 * Dependencies: farmhand_ai API, TTS service
 * Generalization:
 *   - Replace portalAPI with standalone farmhand API
 *   - Remove PortalAuthContext (use standalone auth)
 *   - Remove DeliveryWarningBanner (RB-specific)
 *   - Keep: Chat UI, message handling, TTS toggle, Hank/Laura speakers
 * ============================================
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import portalAPI from '../../services/portalAPI';
import { usePortalAuth } from '../../contexts/PortalAuthContext';
import DeliveryWarningBanner from '../DeliveryWarningBanner';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

// TTS voice settings
const TTS_STORAGE_KEY = 'farmhand_voice_enabled';

// Helper to detect speaker and parse content
const parseSpeaker = (message: string): { speaker: 'hank' | 'laura' | 'both', content: string } => {
  const hankMatch = message.match(/^Hank:\s*/i);
  const lauraMatch = message.match(/^Laura:\s*/i);

  if (hankMatch) {
    return { speaker: 'hank', content: message.replace(/^Hank:\s*/i, '') };
  } else if (lauraMatch) {
    return { speaker: 'laura', content: message.replace(/^Laura:\s*/i, '') };
  }
  return { speaker: 'both', content: message };
};

const FarmhandChat: React.FC = () => {
  const { user } = usePortalAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [showDeliveryInfo, setShowDeliveryInfo] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // TTS (Text-to-Speech) state
  const [voiceEnabled, setVoiceEnabled] = useState(() => {
    const stored = localStorage.getItem(TTS_STORAGE_KEY);
    return stored === 'true';
  });
  const [isPlaying, setIsPlaying] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Toggle voice and persist preference
  const toggleVoice = useCallback(() => {
    setVoiceEnabled(prev => {
      const newValue = !prev;
      localStorage.setItem(TTS_STORAGE_KEY, String(newValue));
      // Stop any playing audio when disabling
      if (!newValue && audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
        setIsPlaying(false);
        setIsSpeaking(false);
      }
      return newValue;
    });
  }, []);

  // Speak response using TTS API
  const speakResponse = useCallback(async (text: string) => {
    if (!voiceEnabled || !text || isSpeaking) return;

    try {
      setIsSpeaking(true);
      setIsPlaying(true);

      // Strip markdown and format for speech
      const cleanText = text
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')  // [link](url) -> link text
        .replace(/\*\*([^*]+)\*\*/g, '$1')        // **bold** -> bold
        .replace(/\*([^*]+)\*/g, '$1')            // *italic* -> italic
        .replace(/[#*_`~]/g, '')                   // Remove markdown chars
        .replace(/\n{2,}/g, '. ')                  // Multiple newlines -> pause
        .replace(/\n/g, ' ')                       // Single newline -> space
        .replace(/\s+/g, ' ')                      // Collapse whitespace
        .trim()
        .substring(0, 4000);                       // Limit length

      if (!cleanText) {
        setIsPlaying(false);
        setIsSpeaking(false);
        return;
      }

      const response = await portalAPI.post('/tts/', { text: cleanText }, {
        responseType: 'blob'
      });

      if (response.status === 200) {
        const audioBlob = response.data;
        const audioUrl = URL.createObjectURL(audioBlob);

        if (audioRef.current) {
          audioRef.current.src = audioUrl;
          audioRef.current.onended = () => {
            setIsPlaying(false);
            setIsSpeaking(false);
            URL.revokeObjectURL(audioUrl);
          };
          audioRef.current.onerror = () => {
            setIsPlaying(false);
            setIsSpeaking(false);
            URL.revokeObjectURL(audioUrl);
          };
          await audioRef.current.play();
        }
      }
    } catch (error) {
      console.error('TTS error:', error);
      setIsPlaying(false);
      setIsSpeaking(false);
    }
  }, [voiceEnabled, isSpeaking]);

  // Stop speaking when component unmounts or chat closes
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
    };
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage: Message = { role: 'user', content: input };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput('');
    setLoading(true);

    try {
      // Send ALL messages including the new one - do not skip any
      const chatHistory = newMessages.map(m => ({
        role: m.role,
        content: m.content
      }));

      const response = await portalAPI.post('/farmhand/chat/', {
        messages: chatHistory
      });

      if (response.data.success) {
        const responseMessage = response.data.message;
        setMessages([...newMessages, {
          role: 'assistant',
          content: responseMessage
        }]);
        // Speak the response if voice is enabled
        if (voiceEnabled) {
          speakResponse(responseMessage);
        }
      } else {
        const errorMessage = response.data.message || "Laura: Sorry, I had trouble with that. Try again?";
        setMessages([...newMessages, {
          role: 'assistant',
          content: errorMessage
        }]);
        if (voiceEnabled) {
          speakResponse(errorMessage);
        }
      }
    } catch (error) {
      const fallbackMessage = "Laura: Hmm, I'm having connection issues. Give Sibyl a shout at Sibyl@theredbridgefarm.com or call (509) 738-9004!";
      setMessages([...newMessages, {
        role: 'assistant',
        content: fallbackMessage
      }]);
      if (voiceEnabled) {
        speakResponse(fallbackMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleQuickAction = (text: string) => {
    setInput(text);
  };

  // Floating bubble (minimized state)
  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        style={styles.floatingBubble}
        className="farmhand-bubble"
        aria-label="Chat with Hank & Laura"
      >
        <img
          src="/images/hank-and-laura.png"
          alt="Hank & Laura"
          style={styles.bubbleImage}
        />
      </button>
    );
  }

  // Expanded chat window
  return (
    <div style={styles.chatContainer} className="farmhand-chat-window">
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <img
            src="/images/hank-and-laura.png"
            alt="Hank & Laura"
            style={styles.headerImage}
          />
          <div style={styles.headerText}>
            <h3 style={styles.headerTitle}>Hank & Laura</h3>
            <p style={styles.headerSubtitle}>Your Farmhand team</p>
          </div>
        </div>
        <div style={styles.headerActions}>
          {/* Voice Toggle Button */}
          <button
            onClick={toggleVoice}
            style={{
              ...styles.voiceButton,
              backgroundColor: voiceEnabled ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
            }}
            aria-label={voiceEnabled ? 'Disable Laura voice' : 'Enable Laura voice'}
            title={voiceEnabled ? 'Voice enabled - click to disable' : 'Voice disabled - click to enable'}
          >
            {isPlaying ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="voice-playing">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
              </svg>
            ) : voiceEnabled ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                <line x1="23" y1="9" x2="17" y2="15" />
                <line x1="17" y1="9" x2="23" y2="15" />
              </svg>
            )}
          </button>
          <button
            onClick={() => setIsOpen(false)}
            style={styles.closeButton}
            aria-label="Close chat"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Hidden audio element for TTS */}
      <audio ref={audioRef} style={{ display: 'none' }} />

      {/* Messages Area */}
      <div style={styles.messagesArea}>
        {/* Welcome message with both avatars */}
        {messages.length === 0 && (
          <div style={styles.welcomeContainer}>
            <div style={styles.welcomeAvatars}>
              <img src="/images/hank.png" alt="Hank" style={styles.welcomeAvatar} />
              <img src="/images/laura.png" alt="Laura" style={{ ...styles.welcomeAvatar, marginLeft: '-8px' }} />
            </div>
            <div style={styles.welcomeBubble}>
              <p style={styles.messageText}>
                <span style={styles.speakerName}>Hank</span>: Hey there! I handle all things farming - feed questions, animal advice, pasture tips.
              </p>
              <p style={{ ...styles.messageText, marginTop: '8px' }}>
                <span style={styles.speakerName}>Laura</span>: And I keep an eye on your weather, production data, and reports. What can we help you with?
              </p>
            </div>
          </div>
        )}

        {/* Delivery Warning Banner - show when chat first opens */}
        {messages.length === 0 && showDeliveryInfo && user?.customer?.id && (
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowDeliveryInfo(false)}
              style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                color: '#6b7280',
                padding: '2px 6px',
                borderRadius: '4px',
              }}
              aria-label="Dismiss delivery info"
            >
              ×
            </button>
            <DeliveryWarningBanner
              customerId={user.customer.id}
              compact
              style={{ fontSize: '12px' }}
            />
          </div>
        )}

        {messages.map((message, index) => (
          <div key={index} style={{ marginBottom: '12px' }}>
            {message.role === 'user' ? (
              <div style={{ ...styles.messageRow, justifyContent: 'flex-end' }}>
                <div style={styles.userMessage}>
                  <p style={styles.messageText}>{message.content}</p>
                </div>
              </div>
            ) : (
              // Render AI message with proper avatars
              (() => {
                const hasBothSpeakers = /Hank:/i.test(message.content) && /Laura:/i.test(message.content);

                if (hasBothSpeakers || message.content.includes('\n\nHank:') || message.content.includes('\n\nLaura:')) {
                  const parts = message.content.split(/(?=(?:^|\n\n?)(?:Hank|Laura):)/gi).filter(p => p.trim());

                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {parts.map((part, idx) => {
                        const { speaker, content } = parseSpeaker(part.trim());
                        return (
                          <div key={idx} style={styles.aiMessageRow}>
                            <img
                              src={speaker === 'hank' ? '/images/hank.png' : speaker === 'laura' ? '/images/laura.png' : '/images/hank-and-laura.png'}
                              alt={speaker === 'hank' ? 'Hank' : speaker === 'laura' ? 'Laura' : 'Hank & Laura'}
                              style={styles.messageAvatar}
                            />
                            <div style={styles.assistantMessage}>
                              {speaker !== 'both' && (
                                <>
                                  <span style={styles.speakerName}>{speaker === 'hank' ? 'Hank' : 'Laura'}</span>
                                  <span style={styles.speakerColon}>: </span>
                                </>
                              )}
                              <span style={styles.messageContent}>{content}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                }

                // Single speaker
                const { speaker, content } = parseSpeaker(message.content);

                return (
                  <div style={styles.aiMessageRow}>
                    <img
                      src={speaker === 'hank' ? '/images/hank.png' : speaker === 'laura' ? '/images/laura.png' : '/images/hank-and-laura.png'}
                      alt={speaker === 'hank' ? 'Hank' : speaker === 'laura' ? 'Laura' : 'Hank & Laura'}
                      style={styles.messageAvatar}
                    />
                    <div style={styles.assistantMessage}>
                      {speaker !== 'both' && (
                        <>
                          <span style={styles.speakerName}>{speaker === 'hank' ? 'Hank' : 'Laura'}</span>
                          <span style={styles.speakerColon}>: </span>
                        </>
                      )}
                      <span style={styles.messageContent}>{content}</span>
                    </div>
                  </div>
                );
              })()
            )}
          </div>
        ))}
        {loading && (
          <div style={styles.aiMessageRow}>
            <img src="/images/hank-and-laura.png" alt="Hank & Laura" style={styles.messageAvatar} />
            <div style={styles.assistantMessage}>
              <p style={styles.messageText}>
                <span style={styles.thinkingDots}>The team is thinking</span>
                <span className="thinking-dots">...</span>
              </p>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Quick Actions - only show initially */}
      {messages.length === 0 && (
        <div style={styles.quickActions}>
          <button
            onClick={() => handleQuickAction("How much feed do I need for 100 Cornish Cross?")}
            style={{ ...styles.quickButton, ...styles.quickButtonGreen }}
          >
            Feed calc
          </button>
          <button
            onClick={() => handleQuickAction("What's the weather looking like this week?")}
            style={{ ...styles.quickButton, ...styles.quickButtonBlue }}
          >
            Weather
          </button>
          <button
            onClick={() => handleQuickAction("How's my FCR looking?")}
            style={{ ...styles.quickButton, ...styles.quickButtonBlue }}
          >
            Performance
          </button>
          <button
            onClick={() => handleQuickAction("My birds seem stressed, what should I check?")}
            style={{ ...styles.quickButton, ...styles.quickButtonGreen }}
          >
            Troubleshoot
          </button>
        </div>
      )}

      {/* Input Area */}
      <div style={styles.inputArea}>
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Ask Hank & Laura..."
          style={styles.input}
          disabled={loading}
        />
        <button
          onClick={sendMessage}
          disabled={loading || !input.trim()}
          style={{
            ...styles.sendButton,
            opacity: loading || !input.trim() ? 0.5 : 1,
            cursor: loading || !input.trim() ? 'not-allowed' : 'pointer'
          }}
        >
          {loading ? (
            <svg style={styles.spinner} viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="32" strokeLinecap="round" />
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
            </svg>
          )}
        </button>
      </div>

      {/* Styles for animations */}
      <style>{`
        .farmhand-bubble {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .farmhand-bubble:hover {
          transform: scale(1.1);
          box-shadow: 0 8px 25px rgba(45, 90, 39, 0.4) !important;
        }
        .farmhand-chat-window {
          animation: slideUp 0.2s ease-out;
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .thinking-dots {
          animation: dots 1.4s infinite;
        }
        @keyframes dots {
          0%, 20% { content: '.'; }
          40% { content: '..'; }
          60%, 100% { content: '...'; }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes voicePulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        .voice-playing {
          animation: voicePulse 1s ease-in-out infinite;
        }
        @media (max-width: 640px) {
          .farmhand-chat-window {
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
            width: 100% !important;
            height: 100% !important;
            border-radius: 0 !important;
          }
        }
      `}</style>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  // Floating bubble
  floatingBubble: {
    position: 'fixed',
    bottom: '20px',
    right: '20px',
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    border: 'none',
    cursor: 'pointer',
    boxShadow: '0 4px 15px rgba(45, 90, 39, 0.3)',
    zIndex: 9999,
    padding: 0,
    overflow: 'hidden',
    background: 'linear-gradient(135deg, #2D5A27 0%, #4a7c43 100%)',
  },
  bubbleImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'center top',
  },

  // Chat container
  chatContainer: {
    position: 'fixed',
    bottom: '20px',
    right: '20px',
    width: '380px',
    height: '520px',
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    zIndex: 9999,
  },

  // Header
  header: {
    background: 'linear-gradient(135deg, #2D5A27 0%, #4a7c43 100%)',
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  headerImage: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    objectFit: 'cover',
    objectPosition: 'center top',
    border: '2px solid rgba(255, 255, 255, 0.3)',
  },
  headerText: {
    display: 'flex',
    flexDirection: 'column',
  },
  headerTitle: {
    color: '#ffffff',
    fontSize: '1rem',
    fontWeight: 600,
    margin: 0,
  },
  headerSubtitle: {
    color: 'rgba(255, 255, 255, 0.8)',
    fontSize: '0.8rem',
    margin: 0,
  },
  closeButton: {
    background: 'transparent',
    border: 'none',
    color: 'rgba(255, 255, 255, 0.8)',
    cursor: 'pointer',
    padding: '8px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '44px',
    minHeight: '44px',
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  voiceButton: {
    background: 'transparent',
    border: 'none',
    color: 'rgba(255, 255, 255, 0.8)',
    cursor: 'pointer',
    padding: '8px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '44px',
    minHeight: '44px',
    transition: 'background-color 0.2s ease',
  },

  // Messages area
  messagesArea: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px',
    backgroundColor: '#f9fafb',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  messageRow: {
    display: 'flex',
    width: '100%',
  },
  userMessage: {
    backgroundColor: '#2D5A27',
    color: '#ffffff',
    borderRadius: '16px 16px 4px 16px',
    padding: '10px 14px',
    maxWidth: '80%',
  },
  assistantMessage: {
    backgroundColor: '#ffffff',
    color: '#374151',
    borderRadius: '16px 16px 16px 4px',
    padding: '10px 14px',
    maxWidth: '80%',
    border: '1px solid #e5e7eb',
  },
  messageText: {
    margin: 0,
    fontSize: '0.9rem',
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
  },
  thinkingDots: {
    color: '#6b7280',
    fontStyle: 'italic',
  },

  // AI message with avatar
  aiMessageRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
  },
  messageAvatar: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    objectFit: 'cover',
    objectPosition: 'center top',
    flexShrink: 0,
    border: '2px solid #ffffff',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  speakerName: {
    fontWeight: 600,
    color: '#1f2937',
  },
  speakerColon: {
    color: '#9ca3af',
    fontWeight: 400,
  },
  messageContent: {
    color: '#374151',
  },

  // Welcome message
  welcomeContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    marginBottom: '8px',
  },
  welcomeAvatars: {
    display: 'flex',
    flexShrink: 0,
  },
  welcomeAvatar: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    objectFit: 'cover',
    objectPosition: 'center top',
    border: '2px solid #ffffff',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  welcomeBubble: {
    backgroundColor: '#ffffff',
    borderRadius: '16px 16px 16px 4px',
    padding: '12px 14px',
    border: '1px solid #e5e7eb',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
  },

  // Quick actions
  quickActions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    padding: '8px 16px',
    backgroundColor: '#f9fafb',
    borderTop: '1px solid #e5e7eb',
  },
  quickButton: {
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '0.75rem',
    fontWeight: 500,
    cursor: 'pointer',
    border: '1px solid',
    transition: 'all 0.15s ease',
  },
  quickButtonGreen: {
    backgroundColor: '#f0fdf4',
    color: '#166534',
    borderColor: '#bbf7d0',
  },
  quickButtonBlue: {
    backgroundColor: '#eff6ff',
    color: '#1e40af',
    borderColor: '#bfdbfe',
  },

  // Input area
  inputArea: {
    display: 'flex',
    gap: '8px',
    padding: '12px 16px',
    backgroundColor: '#ffffff',
    borderTop: '1px solid #e5e7eb',
  },
  input: {
    flex: 1,
    padding: '10px 14px',
    borderRadius: '24px',
    border: '1px solid #d1d5db',
    fontSize: '16px', // Minimum 16px to prevent iOS zoom
    outline: 'none',
    minHeight: '44px', // Touch-friendly
  },
  sendButton: {
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    border: 'none',
    backgroundColor: '#2D5A27',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  spinner: {
    width: '20px',
    height: '20px',
    animation: 'spin 1s linear infinite',
  },
};

export default FarmhandChat;
